import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getProjectWiseCostPerMW, getMonitoringProjectList } from "../../redux/adminUnitReportSlice/adminUnitReportSlice";
import { getAllUnits } from "../../redux/unitSlice/unitSlice";
import CustomTable from '../../components/Common/CustomTable/CustomTable';
import CustomSelectWithFilter from "../../components/Common/CustomSelect/CustomSelectWithFilter";
import CustomMultiSelect from "../../components/Common/CustomSelect/CustomMultiSelect";
import PMGProjectIcon from "../../assets/icons/pmg-project.svg";
import { BarChart3, Loader2 } from "lucide-react";

const PROJECT_TYPE_OPTIONS = [
    { label: "All", value: "ALL" },
    { label: "EPC", value: "EPC" },
    { label: "BTG", value: "BTG" },
    { label: "OTHER", value: "OTHER" },
];

const COST_LEVEL_OPTIONS = [
    { label: "Direct Material", value: "DIRECT_MATERIAL" },
    { label: "Conversion Cost", value: "CONVERSION_COST" },
    { label: "Factory Cost Level", value: "TOTAL_COST" },
];

// Unlike DMProjectWiseReport, this report has no Value Mode filter — every
// figure here is a Crore amount per MW, and "Percent" has no meaning for it.
const VALUE_MODE = "CRORE";

// Only 2 top-level time filter modes. Quarter is a refinement inside YEAR
// mode once a Year is picked (see selectedQuarter below).
const TIME_FILTER_OPTIONS = [
    { label: "Overall", value: "OVERALL" },
    { label: "Year Wise", value: "YEAR" },
];

const toNumber = (val) => {
    // Values from the API can come formatted with thousands separators
    // (e.g. "10,518.00"). parseFloat stops at the first comma, so
    // parseFloat("10,518.00") would wrongly return 10 instead of 10518.
    // Strip commas (and any stray whitespace) before parsing.
    const cleaned = String(val ?? "").replace(/,/g, "").trim();
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
};

// Builds a normalized "IO_NO-REV_NO" key so matching works regardless of
// whether the two APIs send numbers, strings, extra whitespace, or missing REV_NO.
const makeProjectKey = (ioNo, revNo) => {
    const io = String(ioNo ?? "").trim();
    const rev = String(revNo ?? "0").trim();
    return `${io}-${rev}`;
};

// A project without an assigned MW capacity can't be shown in a per-MW
// report — there's nothing to divide the cost by.
const hasMW = (project) => project?.MW !== null && project?.MW !== undefined;

// The financial year (Apr -> Mar) that "today" falls in, expressed as its
// starting calendar year, e.g. April 2026 - March 2027 -> 2026.
const getCurrentFYStart = () => {
    const today = new Date();
    return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

const CURRENT_FY_START = getCurrentFYStart();

// "Q1-2026" -> "2026-2027"
const getFYFromQuarterKey = (key) => {
    const match = /^Q[1-4]-(\d{4})$/.exec(key);
    if (!match) return null;
    const fyStart = Number(match[1]);
    return `${fyStart}-${fyStart + 1}`;
};

const getQuarterLabel = (key) => key.split("-")[0];

const DMMWProjectWiseReport = () => {
    const dispatch = useDispatch();

    // Backend-dependent filters
    const [projectType, setProjectType] = useState("ALL");
    const [costLevel, setCostLevel] = useState("DIRECT_MATERIAL");

    // Master, unfiltered list of projects — used only to populate the
    // "Projects Filter" dropdown. Unlike allProjectsFlat, this does NOT
    // shrink when the API response is filtered down to selected projects.
    const [masterProjects, setMasterProjects] = useState([]);

    // Holds "IO_NO-REV_NO" keys selected in the multi-select (strings, since
    // that's what the select control can compare/highlight). The actual
    // {IO_NO, REV_NO} objects sent to the API are derived from these keys
    // via projectKeyToProject below.
    const [selectedProjectKeys, setSelectedProjectKeys] = useState([]);

    // Frontend-only filters — the API returns every year/quarter in one
    // payload, switching period is purely a frontend re-slice of it.
    const [timeFilter, setTimeFilter] = useState("OVERALL");
    const [selectedYear, setSelectedYear] = useState(""); // FY key, e.g. "2026-2027"
    const [selectedQuarter, setSelectedQuarter] = useState(""); // "" = Full Year, else e.g. "Q1-2026"
    const [selectedUnits, setSelectedUnits] = useState([]);

    const { projectWiseCostPerMW, gettingProjectWiseCostPerMW, monitoringProjectList } = useSelector(
        (state) => state.unitReport
    );
    const { allUnitsList } = useSelector((state) => state.unit);

    const reportData = projectWiseCostPerMW?.data ?? projectWiseCostPerMW ?? null;

    const groups = reportData?.groups ?? [];
    const yearLabels = reportData?.labels ?? [];
    const quarterLabels = reportData?.quarterly_labels ?? [];

    const fetchReport = () => {
        const projectsPayload = selectedProjectKeys
            .map((key) => projectKeyToProject.get(key))
            .filter(Boolean)
            .map(({ IO_NO, REV_NO }) => ({ IO_NO, REV_NO }));

        dispatch(
            getProjectWiseCostPerMW({
                COST_LEVEL: costLevel,
                VALUE_MODE,
                projects: projectsPayload,
            })
        );
    };

    const fetchMonitoringProjects = () => {
        dispatch(
            getMonitoringProjectList({
                PROJECT_TYPE: projectType,
            })
        );
    };

    useEffect(() => {
        dispatch(getAllUnits());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initial load with default filters.
    useEffect(() => {
        setSelectedProjectKeys([]); // reset so masterProjects can repopulate for the new type
        fetchReport();
        fetchMonitoringProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectType]);

    // Re-fetch whenever a backend-dependent filter changes. Skip the very
    // first run — the [projectType] effect above already fires the initial
    // fetch, so this effect would otherwise double it.
    const isReportMount = useRef(true);

    useEffect(() => {
        if (isReportMount.current) {
            isReportMount.current = false;
            return;
        }

        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costLevel, selectedProjectKeys.join(",")]);

    // Every project across every group that has an MW capacity assigned —
    // used to populate the "Selected Projects" dropdown. Projects without an
    // MW value never appear in the table, so they're excluded here too
    // (otherwise picking one would silently show an empty table). Only
    // capture the full list when the response was NOT filtered by a project
    // selection, so the dropdown's option list stays stable.
    useEffect(() => {
        if (selectedProjectKeys.length === 0 && groups.length) {
            setMasterProjects((prev) => {
                const map = new Map(
                    prev.map((project) => [makeProjectKey(project.IO_NO, project.REV_NO), project])
                );

                groups.forEach((group) => {
                    group.projects?.forEach((project) => {
                        if (!hasMW(project)) return;
                        map.set(makeProjectKey(project.IO_NO, project.REV_NO), {
                            IO_NO: project.IO_NO,
                            REV_NO: project.REV_NO,
                            PROJ_NAME: project.PROJ_NAME,
                            PROJECT_TYPE: group.PROJECT_TYPE,
                        });
                    });
                });

                return Array.from(map.values());
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups]);

    // What's actually shown in the "Projects Filter" dropdown — narrowed to
    // the currently selected Project Type, without discarding the other
    // types from masterProjects (they stay cached for when the user
    // switches back).
    const visibleProjectsForFilter = useMemo(() => {
        if (projectType === "ALL") return masterProjects;
        return masterProjects.filter((project) => project.PROJECT_TYPE === projectType);
    }, [masterProjects, projectType]);

    // Maps a "IO_NO-REV_NO" key (what the multi-select stores) back to the
    // full { IO_NO, REV_NO, PROJ_NAME } object (what the API needs).
    const projectKeyToProject = useMemo(() => {
        const map = new Map();
        masterProjects.forEach((project) => {
            map.set(makeProjectKey(project.IO_NO, project.REV_NO), project);
        });
        return map;
    }, [masterProjects]);

    // Union of every unit (MNF_UNIT + UNIT_NAME) seen across every MW-tagged
    // project in the currently loaded response — these are the dynamic
    // middle columns. Units only reachable via MW-null projects don't get a
    // column, since no visible row would ever populate it.
    const unitColumns = useMemo(() => {
        const seen = new Map();
        groups.forEach((group) => {
            group.projects?.forEach((project) => {
                if (!hasMW(project)) return;
                project.units?.forEach((unit) => {
                    if (!seen.has(unit.MNF_UNIT)) {
                        seen.set(unit.MNF_UNIT, {
                            MNF_UNIT: unit.MNF_UNIT,
                            UNIT_NAME: unit.UNIT_NAME,
                        });
                    }
                });
            });
        });

        const unsorted = Array.from(seen.values());
        const list = allUnitsList?.data?.data ?? allUnitsList?.data ?? allUnitsList ?? [];
        const orderMap = new Map();
        list.forEach((u, index) => {
            orderMap.set(u.MNF_UNIT, index);
        });

        return unsorted.sort((a, b) => {
            const indexA = orderMap.has(a.MNF_UNIT) ? orderMap.get(a.MNF_UNIT) : Infinity;
            const indexB = orderMap.has(b.MNF_UNIT) ? orderMap.get(b.MNF_UNIT) : Infinity;
            return indexA - indexB;
        });
    }, [groups, allUnitsList]);

    const unitOptions = useMemo(() => {
        const list = allUnitsList?.data?.data ?? allUnitsList?.data ?? allUnitsList ?? [];
        return list.map((u) => ({
            label: u.UNIT_DISPLAY_NAME,
            value: u.MNF_UNIT,
        }));
    }, [allUnitsList]);

    const visibleUnitColumns = useMemo(() => {
        if (selectedUnits.length === 0) return unitColumns;
        return unitColumns.filter((unit) => selectedUnits.includes(unit.MNF_UNIT));
    }, [unitColumns, selectedUnits]);

    const findUnitByMnfCode = (project, mnfUnit) =>
        project?.units?.find((unit) => unit.MNF_UNIT === mnfUnit) || null;

    // FY labels from the current financial year onward — populate the Year
    // dropdown (shown for YEAR mode).
    const visibleYearLabels = useMemo(() => {
        return yearLabels
            .filter((fy) => Number(fy.split("-")[0]) >= CURRENT_FY_START)
            .sort();
    }, [yearLabels]);

    // Quarter keys grouped under their parent FY, e.g.
    // { "2026-2027": ["Q1-2026", "Q2-2026", "Q3-2026", "Q4-2026"] }.
    const groupedQuarterKeys = useMemo(() => {
        const map = {};

        quarterLabels.forEach((key) => {
            const fy = getFYFromQuarterKey(key);
            if (!fy || Number(fy.split("-")[0]) < CURRENT_FY_START) return;

            if (!map[fy]) map[fy] = [];
            map[fy].push(key);
        });

        Object.keys(map).forEach((fy) => {
            map[fy].sort((a, b) => a.localeCompare(b));
        });

        return map;
    }, [quarterLabels]);

    // Auto-pick a default Year the moment the user switches into YEAR mode,
    // so the table isn't blank while they haven't touched the dropdown yet.
    useEffect(() => {
        if (timeFilter !== "YEAR") return;
        if (!selectedYear && visibleYearLabels.length) {
            setSelectedYear(visibleYearLabels[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeFilter, visibleYearLabels]);

    // Changing the Year invalidates whatever Quarter was picked (it belongs
    // to a different FY) — reset to "Full Year".
    useEffect(() => {
        setSelectedQuarter("");
    }, [selectedYear]);

    // Central accessor: given a record that carries { total, financial_years,
    // quarters } (a project or a unit within it), return the figure for
    // whichever period is currently selected.
    const getPeriodValue = (record) => {
        if (!record) return "0.00";
        if (timeFilter === "YEAR") {
            if (selectedQuarter) {
                return record.quarters?.[selectedQuarter] ?? "0.00";
            }
            return record.financial_years?.[selectedYear] ?? "0.00";
        }
        return record.total ?? "0.00";
    };

    // Normalized set of "IO_NO-REV_NO" keys returned by the monitoring API.
    // Handles a few common response shapes: a bare array, { data: [...] },
    // or { data: { projects: [...] } }.
    const monitoringProjectMap = useMemo(() => {
        let list = monitoringProjectList;

        if (list && !Array.isArray(list)) {
            list = list.data ?? list.projects ?? list.data?.projects ?? [];
        }

        if (!Array.isArray(list)) {
            list = [];
        }

        const map = new Set(
            list.map((item) => makeProjectKey(item.IO_NO, item.REV_NO))
        );

        return map;
    }, [monitoringProjectList]);

    const columns = useMemo(() => {
        const projectNameColumn = {
            title: "Project Name",
            dataIndex: "projectName",
            key: "projectName",
            fixed: "left",
            width: 280,
            render: (text, record) => {
                if (record.isGrandTotal) {
                    return (
                        <span
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                fontWeight: 800,
                                fontSize: 16,
                            }}
                        >
                            <BarChart3 size={18} color="#155EEF" />
                            {text}
                        </span>
                    );
                }

                // Regular project row — the project's type is shown as a
                // subtext under the name instead of a separate EPC/BTG/OTHER
                // group heading.
                return (
                    <div>
                        <div>{text}</div>
                        {record.PROJECT_TYPE && (
                            <div style={{ fontStyle: "italic", fontSize: 12, color: "#667085" }}>
                                {record.PROJECT_TYPE}
                            </div>
                        )}
                    </div>
                );
            },
        };

        const cellRender = (val) => val;

        // Label the Cost/MW column with whichever period is active, so it's
        // clear the figure isn't the all-time total once a Year (and
        // optionally a Quarter within it) is picked.
        let totalCostSuffix = "";
        if (timeFilter === "YEAR" && selectedYear) {
            totalCostSuffix = selectedQuarter
                ? ` (${getQuarterLabel(selectedQuarter)} FY ${selectedYear})`
                : ` (FY ${selectedYear})`;
        }

        const costLevelLabel = COST_LEVEL_OPTIONS.find((option) => option.value === costLevel)?.label || "";

        const totalMWColumn = {
            title: `Total MW`,
            dataIndex: "MW",
            key: "MW",
            width: 200,
            align: "right",
            fixed: "left",
            render: cellRender,
        };

        const totalCostColumn = {
            title: `Overall`,
            dataIndex: "total",
            key: "total",
            width: 170,
            align: "right",
            fixed: "left",
            render: cellRender,
        };

        return [
            projectNameColumn,
            // Total Cost/MW is only meaningful when every unit is included —
            // once the user narrows to specific units, it's hidden so the
            // remaining unit columns aren't mistaken for summing to it.
            ...(selectedUnits.length === 0 ? [totalMWColumn, totalCostColumn] : []),
            ...visibleUnitColumns.map((unit) => ({
                title: unit.UNIT_NAME,
                dataIndex: unit.MNF_UNIT,
                key: unit.MNF_UNIT,
                width: 150,
                align: "right",
                render: cellRender,
            })),
        ];
    }, [visibleUnitColumns, timeFilter, selectedYear, selectedQuarter, selectedUnits, costLevel]);

    const tableData = useMemo(() => {
        const rows = [];

        // Unit columns are the same set in every mode — only the value
        // pulled per cell (via getPeriodValue) changes.
        const valueKeys = visibleUnitColumns.map((column) => column.MNF_UNIT);

        // Totals here are always computed on the frontend from whichever
        // rows actually end up visible — the backend's group.total figures
        // are summed over every project in that group, including ones
        // without an MW value, which this report must hide entirely.
        const grandValueTotals = {};
        valueKeys.forEach((key) => { grandValueTotals[key] = 0; });
        let grandTotal = 0;

        groups.forEach((group) => {
            // When a specific project type is selected (EPC / BTG / OTHER),
            // only include that group's projects.
            if (projectType !== "ALL" && group.PROJECT_TYPE !== projectType) {
                return;
            }

            const filteredProjects = (group.projects?.filter((project) => {
                if (!hasMW(project)) return false;

                if (projectType === "ALL") {
                    return true;
                }

                return monitoringProjectMap.has(
                    makeProjectKey(project.IO_NO, project.REV_NO)
                );
            }) || []).filter((project) => {
                if (selectedUnits.length === 0) return true;
                return (project.units || []).some((u) => selectedUnits.includes(u.MNF_UNIT));
            });

            filteredProjects.forEach((project) => {
                const projectKey = makeProjectKey(project.IO_NO, project.REV_NO);

                const row = {
                    key: projectKey,
                    projectName: project.PROJ_NAME,
                    PROJECT_TYPE: group.PROJECT_TYPE,
                    MW: project.MW,
                };

                valueKeys.forEach((key) => {
                    const unit = findUnitByMnfCode(project, key);
                    const value = getPeriodValue(unit);
                    row[key] = value;
                    grandValueTotals[key] += toNumber(value);
                });

                row.total = getPeriodValue(project);
                grandTotal += toNumber(row.total);

                rows.push(row);
            });
        });

        // Grand total row disabled per requirement — kept here (commented)
        // in case it needs to come back.
        // const grandTotalRow = {
        //     key: "grand-total",
        //     projectName: "GRAND TOTAL",
        //     isGrandTotal: true,
        // };

        // valueKeys.forEach((key) => {
        //     grandTotalRow[key] = grandValueTotals[key].toFixed(2);
        // });

        // grandTotalRow.total = grandTotal.toFixed(2);

        // return [...rows, grandTotalRow];
        return rows;
    }, [
        groups,
        visibleUnitColumns,
        timeFilter,
        selectedYear,
        selectedQuarter,
        projectType,
        monitoringProjectMap,
        selectedUnits,
    ]);

    const quartersForSelectedYear = groupedQuarterKeys[selectedYear] || [];

    return (
        <div className="pb-14">
            <div className="px-5 flex flex-col gap-6 pt-8">
                {/* HEADER */}

                <div className="flex gap-3">
                    <img src={PMGProjectIcon} alt="icon" />

                    <div>
                        <div className="font-inter font-semibold text-[24px] text-[#101828]">
                            DM Analytics - MW Project Wise Report
                        </div>

                        <div className="text-gray-500 text-sm">
                            * All figures are in Rs Crores. Only projects with an assigned MW capacity are shown.
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col gap-6 mt-2">
                    <div className="flex flex-wrap items-start justify-start gap-4 p-4 bg-[#F9FAFB] border border-[#EAECF0] rounded-xl">

                        {/* Type of Project */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Type of Project
                            </span>

                            <CustomSelectWithFilter
                                data={PROJECT_TYPE_OPTIONS}
                                value={projectType}
                                onChange={setProjectType}
                                placeholder="Type of Project"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* Cost Level */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Cost Level
                            </span>

                            <CustomSelectWithFilter
                                data={COST_LEVEL_OPTIONS}
                                value={costLevel}
                                onChange={setCostLevel}
                                placeholder="Cost Level"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div>

                        {/* Time Filter */}
                        {/* <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Time Filter
                            </span>

                            <CustomSelectWithFilter
                                data={TIME_FILTER_OPTIONS}
                                value={timeFilter}
                                onChange={setTimeFilter}
                                placeholder="Time Filter"
                                className="w-[220px]"
                                allowClear={false}
                            />
                        </div> */}

                        {/* Year — appears for Year Wise mode */}
                        {timeFilter === "YEAR" && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-[#667085]">
                                    Year
                                </span>

                                <CustomSelectWithFilter
                                    data={visibleYearLabels.map((year) => ({
                                        label: `FY ${year}`,
                                        value: year,
                                    }))}
                                    value={selectedYear}
                                    onChange={setSelectedYear}
                                    placeholder="Select Year"
                                    className="w-[180px]"
                                    allowClear={false}
                                />
                            </div>
                        )}

                        {/* Quarter — appears as soon as a Year is selected;
                            refines the same Year-mode table instead of
                            requiring a separate "Quarterly" time filter. */}
                        {timeFilter === "YEAR" && selectedYear && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-[#667085]">
                                    Quarter
                                </span>

                                <CustomSelectWithFilter
                                    data={[
                                        { label: "Full Year", value: "" },
                                        ...quartersForSelectedYear.map((key) => ({
                                            label: getQuarterLabel(key),
                                            value: key,
                                        })),
                                    ]}
                                    value={selectedQuarter}
                                    onChange={setSelectedQuarter}
                                    placeholder="Full Year"
                                    className="w-[140px]"
                                />
                            </div>
                        )}

                        {/* Projects Filter */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Projects Filter
                            </span>

                            <CustomMultiSelect
                                data={visibleProjectsForFilter.map((project) => ({
                                    label: project.PROJ_NAME,
                                    value: makeProjectKey(project.IO_NO, project.REV_NO),
                                }))}
                                value={selectedProjectKeys}
                                onChange={setSelectedProjectKeys}
                                placeholder="All Projects"
                                className="w-[280px]"
                            />
                        </div>

                        {/* Unit Filter */}
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-[#667085]">
                                Unit Filter
                            </span>

                            <CustomMultiSelect
                                data={unitOptions}
                                value={selectedUnits}
                                onChange={setSelectedUnits}
                                placeholder="All Units"
                                className="w-[280px]"
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                {gettingProjectWiseCostPerMW ? (
                    <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-8 w-8 animate-spin text-[#155EEF]" />
                    </div>
                ) : (
                    <div>
                        <CustomTable
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            scroll={{ x: "max-content", y: 600 }}
                            rowClassName={(record) => (record.isGrandTotal ? "dm-grand-total-row" : "")}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default DMMWProjectWiseReport;
